import { useMemo } from 'react';

import { getAuthorizedZones, type ZoneAction } from 'api/zones/endpoints/getAuthorizedZones';

import { EditReference } from './EditReference';

// The minimal identifying shape this control deals in — the wire
// format saves only `name`, so the editable entity only needs that
// field. `AuthorizedZone` (with zone_id + description) is
// structurally assignable to this, so the curried loader can pass
// straight through.
export interface ZoneReference {
  readonly name: string;
}

interface Props {
  readonly value: ZoneReference | null;
  readonly onChange: (value: ZoneReference | null) => void;
  readonly resourceType: string;
  readonly action: ZoneAction;
  readonly label?: string;
  readonly mandatory?: boolean;
  readonly disabled?: boolean;
}

const getKey = (z: ZoneReference) => z.name;
const getLabel = (z: ZoneReference) => z.name;

/**
 * Searchable dropdown for authorised application Zones (NOT IANA time
 * zones — `EditTimeZoneReference` is the other one). Caller passes
 * `resourceType` + `action` so the backend can filter the LOV to the
 * zones the current user can act on.
 */
export const EditZoneReference = ({ value, onChange, resourceType, action, label = 'Zone', mandatory, disabled }: Props) => {
  // Memoise the curried loader so the EditReference effect doesn't
  // re-fire on every render — same (resourceType, action) yields the
  // same function reference.
  const source = useMemo(() => getAuthorizedZones(resourceType, action), [resourceType, action]);
  return (
    <EditReference<ZoneReference>
      label={label}
      value={value}
      onChange={onChange}
      source={source}
      getKey={getKey}
      getLabel={getLabel}
      mandatory={mandatory}
      disabled={disabled}
    />
  );
};
